/**
 * Shared due-date urgency helpers for job cards (dispatch backlog, day
 * swimlane, missed-jobs). Previously duplicated inline in backlog-panel.tsx.
 *
 * Thresholds:
 *   - "overdue" → due_by_date is strictly before today (red)
 *   - "soon"    → due today or within the next 14 days (amber)
 *   - null      → no deadline set, or deadline > 14 days out (no pill)
 *
 * Comparison is on calendar-day boundaries in the user's local timezone —
 * the database stores DATE (no timezone) and the dispatcher is mentally
 * working in their local clock, so a UTC off-by-one would be confusing.
 *
 * NOTE: this is the CLIENT helper. The server has its own timezone-aware
 * `dueUrgency()` in src/server/routes/opsOpportunity.ts (uses the dispatch
 * calendar TZ); the two are intentionally separate (different build roots).
 */
export type DueState = "overdue" | "soon" | "scheduled_past_due" | null;

/** Amber window: due today through this many days out. */
export const DUE_SOON_DAYS = 14;

/**
 * @param scheduledStart Pass the job's scheduled start (ISO datetime or
 * date) when known. Prompt 23 item 3: a WO complete-by that's already past
 * is NOT an alarm when the office knowingly scheduled the visit after it —
 * that returns "scheduled_past_due" (informational chip), never "overdue".
 */
export function classifyDueState(dueByDate: string | null | undefined, scheduledStart?: string | null): DueState {
    if (!dueByDate) return null;
    const due = new Date(`${dueByDate}T00:00:00`);
    if (Number.isNaN(due.getTime())) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dueDay = new Date(due);
    dueDay.setHours(0, 0, 0, 0);
    const diffDays = Math.round((dueDay.getTime() - today.getTime()) / 86_400_000);
    if (diffDays < 0) {
        if (scheduledStart) {
            const sched = new Date(scheduledStart);
            if (!Number.isNaN(sched.getTime())) {
                const schedDay = new Date(sched);
                schedDay.setHours(0, 0, 0, 0);
                if (schedDay.getTime() > dueDay.getTime()) return "scheduled_past_due";
            }
        }
        return "overdue";
    }
    if (diffDays <= DUE_SOON_DAYS) return "soon";
    return null;
}

/** Long-form informational text for the scheduled_past_due chip. */
export function scheduledPastDueTitle(dueByDate: string): string {
    const d = new Date(`${dueByDate}T00:00:00`);
    const label = Number.isNaN(d.getTime())
        ? dueByDate
        : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    return `WO complete-by ${label} — scheduled past due date`;
}

/**
 * ISO date → "Due M/D" in local time; the dispatcher reads "Due 5/15" at a
 * glance without scanning a four-digit year.
 */
export function formatDuePillLabel(dueByDate: string): string {
    const d = new Date(`${dueByDate}T00:00:00`);
    if (Number.isNaN(d.getTime())) return `Due ${dueByDate}`;
    return `Due ${d.getMonth() + 1}/${d.getDate()}`;
}
